<script setup>
import {to} from "await-to-js";
import {ref, reactive, onMounted, computed} from "vue";
import {cloneDeep} from "lodash";
import {interceptor} from "@dutypay/utilities";
import {useEnvStore} from "@dutypay/store-modules";
import {ElNotification} from "element-plus";

const loading = ref(false);
let isAddPermissionGroupDialogVisible = ref(false);

const topScroll = ref(null);
const bottomScroll = ref(null);

const addPermissionGroupFormBlank =
    {
      name: "",
      permission: {
        ids: []
      },
      version: 0,
      description: {
        text: "",
        url: ""
      },
      isEditable: 1,
      sp: {
        permission: {
          group: {
            id: ""
          }
        }
      }
    };

const addPermissionGroupForm = ref({
  name: "",
  permission: {
    ids: []
  },
  version: 0,
  description: {
    text: "",
    url: ""
  },
  isEditable: 1,
  sp: {
    permission: {
      group: {
        id: ""
      }
    }
  }
});

const isEditPermissionGroupDialogVisible = ref(false);
const editPermissionGroupFormBlank = {
  name: "",
  hs: {
    permission: {
      group: {
        id: ""
      }
    }
  },
  version: 0,
  permission: {
    ids: []
  },
  description: {
    text: "",
    url: ""
  },
  isEditable: 1,
  sp: {
    permission: {
      group: {
        id: ""
      }
    }
  }
};
const editPermissionGroupForm = ref({
  hs: {
    permission: {
      group: {
        id: ""
      }
    }
  },
  name: "",
  permission: {
    ids: []
  },
  version: 0,
  description: {
    text: "",
    url: ""
  },
  isEditable: 1,
  sp: {
    permission: {
      group: {
        id: ""
      }
    }
  }
});
const permissionGroups = ref([]);

const permissions = ref([]);


const queriedPermissionGroupCount = ref(0);
const totalPermissionGroupCount = ref(0);
const envStore = useEnvStore();

const pageSizeOptions = ref([
  {label: '10', value: "10"},
  {label: '25', value: '25'},
  {label: '50', value: '50'}
]);

const parameters = reactive({
  pageSize: pageSizeOptions.value[0].value,
  search: "",
  pageNumber: "1",
});

async function updatePageSize(size) {
  parameters.pageSize = size;
  parameters.pageNumber = "1";
  await getPermissionGroups()
}

const hasPreviousPage = computed(() => {
  return parseInt(parameters.pageNumber, 10) > 1;
});

async function getPreviousPage() {
  parameters.pageNumber = (parseInt(parameters.pageNumber, 10) - 1).toString();
  await getPermissionGroups();
}

const hasNextPage = computed(() => {
  return (queriedPermissionGroupCount.value < totalPermissionGroupCount.value);
});

async function getNextPage() {
  parameters.pageNumber = (parseInt(parameters.pageNumber, 10) + 1).toString();
  await getPermissionGroups();
}

async function getPermissions() {
  loading.value = true;
  let err, res;
  [err, res] = await to(
      interceptor.get(`${envStore.apiUrls.dutypay}/api/v0/permissions`, {})
  );
  if (err) {
    loading.value = false;
    return;
  }
  permissions.value = [];
  permissions.value = res.data.result;
  loading.value = false;
}

async function getPermissionGroups() {
  loading.value = true;
  let err, res;
  [err, res] = await to(
      interceptor.get(`${envStore.apiUrls.dutypay}/api/v0/permission-groups`, {
        params: {
          search: parameters.search,
          pageNumber: parameters.pageNumber,
          pageSize: parameters.pageSize,
        },
      })
  );
  if (err) {
    loading.value = false;
    return;
  }
  permissionGroups.value = [];
  permissionGroups.value = res.data.result.permissionGroupList;
  queriedPermissionGroupCount.value = res.data.result.sqlQueriedPermissionGroupCurrentCount;
  totalPermissionGroupCount.value = res.data.result.sqlQueriedPermissionGroupTotalCount;
  loading.value = false;
}

async function getPermissionGroup(id) {
  let err, res;
  [err, res] = await to(
      interceptor.get(`${envStore.apiUrls.dutypay}/api/v0/permission-groups/${id}`, {})
  );
  if (err) {
    ElNotification({
      title: "Error on getting permission with ID " + id,
      message: err.response.data,
      type: "error",
    });
    return;
  }
  return res.data.result;
}

async function addPermissionGroup(isNewInstance = false) {
  try {
    // Overwrite addPermissionGroupForm if you try to create a new instance
    if (isNewInstance) {
      addPermissionGroupForm.value = cloneDeep(editPermissionGroupForm.value)
      delete addPermissionGroupForm.value['hs']
    }
    addPermissionGroupForm.value.permission.ids.sort();
    await (
        interceptor.post(
            `${envStore.apiUrls.dutypay}/api/v0/permission-groups`,
            addPermissionGroupForm.value
        )
    );
    ElNotification({
      title: "PermissionGroup successfully added.",
      type: "success",
    });
    loading.value = false;

    await getPermissionGroups();

    if (isNewInstance) {
      editPermissionGroupForm.value = cloneDeep(editPermissionGroupFormBlank);
      isEditPermissionGroupDialogVisible.value = false;
      isAddPermissionGroupDialogVisible.value = false;
    } else {
      addPermissionGroupForm.value = cloneDeep(addPermissionGroupFormBlank);
      isAddPermissionGroupDialogVisible.value = false;
    }
  } catch (err) {
    loading.value = false;
    ElNotification({
      title: "Error on adding permission",
      message: err.response.data,
      type: "error",
    });
    isAddPermissionGroupDialogVisible.value = false;
    isEditPermissionGroupDialogVisible.value = false;
  }
}


async function handleEditPermissionGroup(index, permission) {
  let id = permission.hs.permission.group.id;
  let err, permissionFromServer;
  [err, permissionFromServer] = await to(getPermissionGroup(id));
  if (err) {
    ElNotification({
      title: "Error on getting permission " + id,
      message: err.response.data,
      type: "error",
    });
  }
  editPermissionGroupForm.value = permissionFromServer;
  isEditPermissionGroupDialogVisible.value = true;
}

async function deletePermissionGroup(index, permission) {
  try {
    await (
        interceptor.delete(`${envStore.apiUrls.dutypay}/api/v0/permission-groups/${permission.hs.permission.group.id}`));
    ElNotification({
      title: "PermissionGroup successfully deleted.",
      type: "success",
    });
    loading.value = false;
    await getPermissionGroups();

  } catch (err) {
    loading.value = false;
    ElNotification({
      title: "Error on deleting permission",
      message: err.response.data,
      type: "error",
    });
  }
}

async function editPermissionGroup() {
  try {
    editPermissionGroupForm.value.permission.ids.sort();
    await (
        interceptor.patch(
            `${envStore.apiUrls.dutypay}/api/v0/permission-groups/${editPermissionGroupForm.value.hs.permission.group.id}`,
            editPermissionGroupForm.value
        )
    );
    ElNotification({
      title: "PermissionGroup successfully edited.",
      type: "success",
    });
    loading.value = false;
    editPermissionGroupForm.value = cloneDeep(editPermissionGroupFormBlank);
    isEditPermissionGroupDialogVisible.value = false;
    await getPermissionGroups();

  } catch (err) {
    loading.value = false;
    ElNotification({
      title: "Error on editing permission",
      message: err.response.data,
      type: "error",
    });
    isEditPermissionGroupDialogVisible.value = false;
  }
}

onMounted(async () => {
  if (topScroll.value && bottomScroll.value) {
    topScroll.value.addEventListener("scroll", () => {
      bottomScroll.value.scrollLeft = topScroll.value.scrollLeft;
    });

    bottomScroll.value.addEventListener("scroll", () => {
      topScroll.value.scrollLeft = bottomScroll.value.scrollLeft;
    });
  }

  await getPermissionGroups();
  await getPermissions();
});
</script>

<script>
import PaginationNavigationComponent from '../../components/PaginationNavigationComponent.vue';
import PaginationFilterOptionsRadioComponent from '../../components/PaginationFilterOptionsRadioComponent.vue';

export default {
  name: "PermissionGroupsPage",
  components: {
    PaginationNavigationComponent,
    PaginationFilterOptionsRadioComponent
  },
};
</script>

<template>
  <div>
    <el-row :gutter="20">
      <el-col :span="24">
        <h1>
          PermissionGroups
          <el-button v-if="envStore.appEnv === 'local'" type="success"
                     @click="isAddPermissionGroupDialogVisible = true">
            Add
          </el-button>
        </h1>
      </el-col>
    </el-row>
    <el-row :gutter="20">
      <el-col :span="8" :xl="8" :lg="8" :sm="24" :xs="24" class="searchform">
        <el-form-item style="flex-grow: 1">
          <el-input
              v-model="parameters.search"
              @click="getPermissionGroups()"
              @change="parameters.pageNumber = 1"
              placeholder="Search for hs.id, name"
              :clearable="true"
          >
          </el-input>
        </el-form-item>
        <el-form-item style="padding-left: 1rem; flex-grow: 0">
          <el-button type="primary" @click="getPermissionGroups"> Search</el-button>
        </el-form-item>
      </el-col>
      <el-col
          :span="16"
          :xl="16"
          :lg="16"
          :sm="24"
          :xs="24"
          class="pagesize"
          style="display: flex; justify-content: flex-end"
      >
        <pagination-filter-options-radio-component
            label="PermissionGroups per page"
            v-model="parameters.pageSize"
            :options="pageSizeOptions"
            :formItemStyle="'padding-top: 2px;'"
            :onChange="updatePageSize"
        ></pagination-filter-options-radio-component>
      </el-col>
    </el-row>
    <el-row>
      <pagination-navigation-component
          :leftIconClass="'el-icon--left'"
          :rightIconClass="'el-icon--right'"
          :previousLabel="'Previous'"
          :nextLabel="'Next'"
          :hasPrevious="hasPreviousPage"
          :hasNext="hasNextPage"
          :onPrevious="getPreviousPage"
          :onNext="getNextPage"
      ></pagination-navigation-component>
    </el-row>
    <el-row>
      <el-col :span="24">
        <div style="width: 100%; overflow: hidden; position: relative;">
          <div ref="topScroll" class="top-scroll"
               style="overflow-x: auto; width: 100%; position: absolute; top: 0; height: 20px;">
            <div style="width: 2400px; height: 1px;"></div>
          </div>
          <div ref="bottomScroll" class="bottom-scroll" style="overflow-x: auto; width: 100%; margin-top: 20px;">
            <el-table :data="permissionGroups" stripe style="min-width: 2400px;width: 100%">
              <el-table-column prop="version" label="Version"/>
              <el-table-column prop="hs.permission.group.id" label="HubSystem Permission Group ID"/>
              <el-table-column label="HubSystem Permission List">
                <template #default="scope">
                  <div>
                    <p>{{ scope.row.permission.names }} </p>
                    <p style="font-size: smaller; ">
                      ({{ scope.row.permission.ids }}) </p>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="name" label="Name"/>
              <el-table-column prop="sp.permission.group.id" label="SP-Permission-Group-ID"/>
              <el-table-column prop="description.text" label="Description as text"/>
              <el-table-column prop="description.url" label="Description URL"/>
              <el-table-column label="Actions">
                <template #default="scope">

                  <el-button v-if="!scope.row.isEditable" type="info"
                             @click="handleEditPermissionGroup(scope.$index, scope.row)"
                  > Details
                  </el-button>

                  <el-button v-if="scope.row.isEditable" type="warning"
                             @click="handleEditPermissionGroup(scope.$index, scope.row)"
                  >Edit
                  </el-button>
                  <el-button v-if="scope.row.isEditable" type="danger"
                             @click="deletePermissionGroup(scope.$index, scope.row)"
                  >Delete
                  </el-button
                  >
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-col>
    </el-row>

    <el-dialog
        v-model="isAddPermissionGroupDialogVisible"
        title="Add PermissionGroup"
        width="1000px"
        align-center
        draggable
    >
      <el-form
          :model="addPermissionGroupForm"
          label-position="left"
          label-width="300px"
          style="max-width: 1000px"
      >
        <el-form-item label="HubSystem Permission ID">
          <el-select v-model="addPermissionGroupForm.permission.ids" multiple placeholder="Select">
            <el-option
                v-for="item in permissions"
                :key="item.hs.permission.id"
                :label="`${item.name}`"
                :value="item.hs.permission.id"
            >
              <template #default>
                <el-tooltip effect="dark" :content="item.description.text" placement="top">
                  <span> {{ item.name }}</span>
                </el-tooltip>
              </template>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Name">
          <el-input v-model="addPermissionGroupForm.name"/>
        </el-form-item>
        <el-form-item label="SP Permission ID">
          <el-input v-model="addPermissionGroupForm.sp.permission.group.id"/>
        </el-form-item>
        <el-form-item label="Description">
          <el-input v-model="addPermissionGroupForm.description.text"/>
        </el-form-item>
        <el-form-item label="Description URL">
          <el-input v-model="addPermissionGroupForm.description.url"/>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isAddPermissionGroupDialogVisible = false"
          >Cancel</el-button
          >
          <el-button type="primary" @click="addPermissionGroup(false)">
            Add PermissionGroup
          </el-button>
        </span>
      </template>
    </el-dialog>


    <el-dialog
        v-model="isEditPermissionGroupDialogVisible"
        :title="'Edit PermissionGroup \'' + editPermissionGroupForm.hs.permission.group.id + '\''"
        width="1000px"
        align-center
        draggable
    >
      <el-form
          :model="editPermissionGroupForm"
          label-position="left"
          label-width="300px"
          style="max-width: 1000px"
      >
        <el-form-item label="Name">
          <el-input v-model="editPermissionGroupForm.name" disabled/>
        </el-form-item>
        <el-form-item label="SP Permission ID">
          <el-input v-model="editPermissionGroupForm.sp.permission.group.id" disabled/>
        </el-form-item>
        <el-form-item label="Permission IDs">
          <el-select v-model="editPermissionGroupForm.permission.ids" multiple placeholder="Select">
            <el-option
                v-for="item in permissions"
                :key="item.hs.permission.id"
                :label="`${item.name}`"
                :value="item.hs.permission.id"
            >
              <template #default>
                <el-tooltip effect="dark" :content="item.description.text" placement="top">
                  <span> {{ item.name }}</span>
                </el-tooltip>
              </template>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Description">
          <el-input v-model="editPermissionGroupForm.description.text"/>
        </el-form-item>
        <el-form-item label="Description URL">
          <el-input v-model="editPermissionGroupForm.description.url"/>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isEditPermissionGroupDialogVisible = false"
          >Cancel</el-button
          >
          <el-button v-if="editPermissionGroupForm.isEditable" type="primary" @click="editPermissionGroup">
            Edit PermissionGroup
          </el-button>
          <el-button v-else type="primary" @click="addPermissionGroup(true)">
            Create as new instance
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.searchform {
  display: inline-flex !important;
}

.top-scroll,
.bottom-scroll {
  scrollbar-width: thin;
  scrollbar-color: #888 transparent;
}

</style>

<i18n>
{
  "en-US": {
  }
}
</i18n>
